import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
// Relative (not @/) imports: this module is MF-exposed as
// ./authenticatedApiClient. Today's emitted d.ts happens not to reference these
// modules, so the alias does not leak — but it would the moment an exported
// signature mentions one of their types, and MF DTS consumers cannot resolve @/.
import { useAuthStore } from "../auth/auth.store";
import { getAppSettings } from "../config/app-settings";

/**
 * Authenticated Axios Client
 *
 * Automatically injects Bearer token into all requests.
 * Handles 401 responses by refreshing token and retrying.
 *
 * Usage in shell:
 * ```typescript
 * import { createAuthenticatedApiClient } from '@/shared/api/axios'
 * const authenticatedApiClient = createAuthenticatedApiClient()
 * const response = await authenticatedApiClient.get('/api/orders')
 * ```
 *
 * Usage in MFEs:
 * ```typescript
 * import { createAuthenticatedApiClient } from 'shipbob_network_intelligence/authenticatedApiClient'
 * const authenticatedApiClient = createAuthenticatedApiClient()
 * const data = await authenticatedApiClient.post('/api/orders', orderData)
 * ```
 *
 * Note: this calls useAuthStore(), which requires an active Pinia instance.
 * Only call this after the shell (or the standalone dev harness) has booted
 * and installed Pinia.
 */

function attachAuthInterceptors(client: AxiosInstance): AxiosInstance {
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const authStore = useAuthStore();
      const token = await authStore.getAccessToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      console.error("[API] Request error:", error);
      return Promise.reject(error);
    },
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        console.log("[API] 401 Unauthorized - attempting token refresh");

        try {
          const authStore = useAuthStore();
          await authStore.refreshToken();

          return client(originalRequest);
        } catch (refreshError) {
          console.error("[API] Token refresh failed, logging out");

          const authStore = useAuthStore();
          try {
            await authStore.logout();
            // signoutRedirect navigates to the identity server; no hard-redirect needed.
          } catch {
            // OIDC sign-out failed; force to /login so the user is not stuck.
            window.location.href = `${import.meta.env.BASE_URL}login`;
          }

          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    },
  );

  return client;
}

export function createAuthenticatedApiClient(baseURL?: string): AxiosInstance {
  const resolvedBaseUrl = baseURL || getAppSettings().apiBaseUrl;

  const client = axios.create({
    baseURL: resolvedBaseUrl,
    timeout: 30000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return attachAuthInterceptors(client);
}
